import { Navigate, useLocation } from 'react-router-dom';
import type { DivisionId } from '@atlas/shared';
import { canReachDivision } from '@atlas/shared';
import { useAuth } from '../contexts/AuthContext';
import { useDivision } from '../contexts/DivisionContext';
import { useHiddenFeatures } from '../hooks/useHiddenFeatures';
import { navItemForPath } from '../config/navConfig';
import { navFeatureId } from '../config/featureIds';
import { ADMIN_TABS, type AdminTab } from '../pages/admin/adminTabs.config';
import { useVisibleAdminTabs } from '../pages/admin/useVisibleAdminTabs';

/** Whether `path` is this tab's page or something nested under it. */
function matchesAdminTab(tab: AdminTab, path: string): boolean {
  return path === tab.path || path.startsWith(`${tab.path}/`);
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  /**
   * Divisions permitted to reach this route (scope axis). The user must be able
   * to reach at least one (org members reach all; an absent claim resolves to
   * org). Unreachable users are redirected home. Empty/undefined = no gate.
   */
  requiredDivisions?: DivisionId[];
  allowPasswordChange?: boolean;
  /**
   * Skip the "must have chosen a division" redirect. Set on the launch screen
   * itself (and any auth route that should render before a division exists) to
   * avoid a redirect loop.
   */
  skipDivisionGate?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requiredDivisions,
  allowPasswordChange = false,
  skipDivisionGate = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, mustChangePassword } = useAuth();
  const { division, isLoaded: divisionLoaded } = useDivision();
  const { isHidden } = useHiddenFeatures();
  const visibleAdminTabs = useVisibleAdminTabs();
  const location = useLocation();

  const loadingScreen = (
    <div className="loading-container">
      <div className="spinner" />
      <p>Loading...</p>
    </div>
  );

  if (isLoading) {
    return loadingScreen;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to change password page if user must change password
  // unless they're already on a page that allows password change
  if (mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Launch-screen gate: once past auth + password, a user with no chosen
  // division is sent to the picker. Wait for the server value to resolve first
  // so we never redirect on a transient null during load.
  if (!skipDivisionGate) {
    if (!divisionLoaded) {
      return loadingScreen;
    }
    if (!division) {
      return <Navigate to="/select-division" state={{ from: location }} replace />;
    }
  }

  if (requiredRoles && user) {
    const hasRequiredRole = requiredRoles.some(role => {
      // If admin is required, both admin and pm can access
      if (role === 'admin') {
        return user.roles.includes('admin') || user.roles.includes('pm');
      }
      return user.roles.includes(role);
    });
    if (!hasRequiredRole) {
      return <Navigate to="/products" replace />;
    }
  }

  // Feature-visibility gate: a page whose nav entry is hidden for this user
  // (globally, or for every role they hold) is not reachable by typing its URL
  // either. Home is NON_HIDEABLE, so this can never redirect onto itself.
  const navItem = navItemForPath(location.pathname);
  if (navItem && isHidden(navFeatureId(navItem.to))) {
    return <Navigate to="/" replace />;
  }

  // Same gate for the admin panel, which has no nav entry of its own below
  // /admin. A tab is reachable when its own switch is on — and, for the
  // hard-locked ones, when the user holds the role as well; `visibleTabs`
  // resolves both. Landing on /admin itself needs at least one tab to open,
  // since the panel is otherwise an empty shell.
  if (location.pathname.startsWith('/admin')) {
    const tab = visibleAdminTabs.roleTabs.find((candidate) =>
      matchesAdminTab(candidate, location.pathname),
    );
    const reachable = tab
      ? visibleAdminTabs.visibleTabs.includes(tab)
      : location.pathname !== '/admin' || visibleAdminTabs.visibleTabs.length > 0;
    if (!reachable) {
      return <Navigate to="/" replace />;
    }
    // A path under /admin that matches no tab this user's roles allow (an
    // account-security tab, for someone without the role) is refused too.
    if (
      !tab &&
      location.pathname !== '/admin' &&
      ADMIN_TABS.some((candidate) => matchesAdminTab(candidate, location.pathname))
    ) {
      return <Navigate to="/" replace />;
    }
  }

  // Division scope gate: reach requires membership in one of requiredDivisions
  // (org reaches all; absent claim → org → reaches all, so no lockout on a
  // pre-existing session that predates the divisions claim).
  if (requiredDivisions && requiredDivisions.length > 0 && user) {
    const canReach = requiredDivisions.some((d) => canReachDivision(user.divisions, d));
    if (!canReach) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
